const A4_WIDTH_PX = 794;
const A4_HEIGHT_PX = 1123;
const PAGE_MARGIN_PX = 33;

const {
  ANSWER_TEMPLATE,
  normalizeAnswerTemplate,
} = require("../constants/answerTemplates");
const { getAnswerLayout } = require("./answerLayout");

const loadJsPdf = async () => {
  const module = await import("jspdf");
  return module.jsPDF || module.default?.jsPDF || module.default;
};

const sanitizePdfFilename = (subject) => {
  const safeSubject = String(subject || "답안지")
    .replace(/[\\/:*?"<>|]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 60);
  return `${safeSubject || "답안지"}_답안지.pdf`;
};

const getFontUrl = () => {
  const fontAsset = require("../assets/NotoSansKR-Regular.woff");
  return new URL(fontAsset.default || fontAsset, document.baseURI).href;
};

const createFontStyle = (fontUrl = getFontUrl()) => {
  const style = document.createElement("style");
  // 새 인쇄 창에도 동일한 웹폰트를 제공한다. 상대 경로는 about:blank에서
  // 해석할 수 없으므로 절대 URL을 사용한다.
  style.textContent = ["IBT Noto Sans KR", "Noto Sans KR"]
    .map(
      (name) => `@font-face {
      font-family: "${name}";
      src: url(${JSON.stringify(fontUrl)}) format("woff");
      font-style: normal; font-weight: 400;
    }`,
    )
    .join("\n");
  return style;
};

const waitForAnswerFont = async (documentRef, layout) => {
  if (!documentRef.fonts) return;
  // fonts.ready만 기다리면 아직 사용하지 않은 글꼴은 로드되지 않을 수 있다.
  await documentRef.fonts.load(
    `${layout.textStyle.fontSize} ${layout.textStyle.fontFamily.split(",")[0]}`,
    "한글 ABC ①",
  );
  await documentRef.fonts.ready;
};

const createAnswerFlow = (html, layout) => {
  const flow = document.createElement("div");
  const source = document.createElement("template");
  source.innerHTML = html || "";
  // 저장된 답안의 블록/빈 줄/정렬은 보존하되, 실행 코드와 외부 리소스,
  // 작성 화면에 없는 글꼴·너비 등은 출력 DOM으로 가져오지 않는다.
  const copyNode = (node, parent) => {
    if (node.nodeType === 3) {
      parent.appendChild(document.createTextNode(node.textContent));
      return;
    }
    if (node.nodeType !== 1) return;
    if (
      /^(SCRIPT|STYLE|IFRAME|OBJECT|EMBED|IMG|VIDEO|AUDIO|LINK|META)$/.test(
        node.tagName,
      )
    )
      return;
    if (node.tagName === "BR") {
      parent.appendChild(document.createElement("br"));
      return;
    }
    if (node.tagName === "DIV" || node.tagName === "P") {
      const block = document.createElement("div");
      const alignment = node.style.textAlign || node.getAttribute("align");
      if (["left", "center", "right", "justify"].includes(alignment)) {
        block.style.textAlign = alignment;
      }
      Array.from(node.childNodes).forEach((child) => copyNode(child, block));
      parent.appendChild(block);
      return;
    }
    Array.from(node.childNodes).forEach((child) => copyNode(child, parent));
  };
  Array.from(source.content.childNodes).forEach((node) => copyNode(node, flow));
  Object.assign(flow.style, layout.textStyle, {
    boxSizing: "border-box",
    width: `${layout.contentWidth}px`,
    minHeight: `${layout.lineHeight}px`,
    margin: "0",
    padding: "0",
    border: "0",
    textAlign: "start",
  });
  flow.setAttribute("data-answer-export-content", "true");
  return flow;
};

const createAnswerSheetElement = async ({ html, subject, templateType }) => {
  const normalizedTemplateType = normalizeAnswerTemplate(templateType);
  const layout = getAnswerLayout(normalizedTemplateType);
  const isMinistry =
    normalizedTemplateType === ANSWER_TEMPLATE.MINISTRY_OF_JUSTICE;
  const root = document.createElement("div");
  root.setAttribute("data-answer-export-root", "true");
  root.setAttribute("data-answer-template", normalizedTemplateType);
  root.setAttribute("aria-label", `${subject || "답안지"} 답안지`);
  Object.assign(root.style, {
    position: "absolute",
    left: "-10000px",
    top: "0",
    width: `${A4_WIDTH_PX}px`,
    background: "#ffffff",
    fontFamily: '"Noto Sans KR", "Malgun Gothic", sans-serif',
  });
  root.appendChild(createFontStyle());
  const flow = createAnswerFlow(html, layout);
  root.appendChild(flow);
  document.body.appendChild(root);
  try {
    await waitForAnswerFont(document, layout);
    // 원본 에디터의 폭과 CSS로 브라우저가 실제 배치한 높이를 잰다.
    // 49개씩 자르거나 종이 폭에서 재배치하지 않는다.
    const lineCount = Math.max(
      1,
      Math.ceil(flow.scrollHeight / layout.lineHeight),
    );
    const neededPages =
      1 +
      Math.ceil(
        Math.max(0, lineCount - layout.firstPageRows) / layout.normalPageRows,
      );
    const pageCount = Math.max(
      layout.minimumPageCount,
      Math.ceil(neededPages / layout.pageGroupSize) * layout.pageGroupSize,
    );
    const sheetWidth = layout.width + layout.gutterWidth;
    const scale = (A4_WIDTH_PX - PAGE_MARGIN_PX * 2) / sheetWidth;
    let firstLine = 0;
    flow.remove();

    for (let pageIndex = 0; pageIndex < pageCount; pageIndex += 1) {
      const capacity =
        pageIndex === 0 ? layout.firstPageRows : layout.normalPageRows;
      const page = document.createElement("section");
      page.setAttribute("data-answer-export-page", String(pageIndex + 1));
      page.setAttribute("data-answer-start-line", String(firstLine + 1));
      page.setAttribute("data-answer-row-count", String(capacity));
      Object.assign(page.style, {
        boxSizing: "border-box",
        position: "relative",
        width: `${A4_WIDTH_PX}px`,
        height: `${A4_HEIGHT_PX}px`,
        padding: `${PAGE_MARGIN_PX}px`,
        margin: "0",
        background: "#ffffff",
        breakAfter: pageIndex === pageCount - 1 ? "auto" : "page",
        pageBreakAfter: pageIndex === pageCount - 1 ? "auto" : "always",
        overflow: "hidden",
      });
      const sheet = document.createElement("div");
      Object.assign(sheet.style, {
        width: `${sheetWidth}px`,
        transform: `scale(${scale})`,
        transformOrigin: "top left",
      });
      if (pageIndex === 0) {
        const spacer = document.createElement("div");
        spacer.style.height = `${layout.topSpacerHeight}px`;
        sheet.appendChild(spacer);
        if (!isMinistry) {
          ["문형", "사례형"].forEach((text, index) => {
            const header = document.createElement("div");
            header.textContent = text;
            Object.assign(header.style, {
              marginLeft: `${layout.gutterWidth}px`,
              width: `${layout.width}px`,
              height: "40px",
              lineHeight: "40px",
              textAlign: "center",
              fontSize: "24px",
              fontWeight: "700",
              color: index === 0 ? "#ffffff" : "#003664",
              background: index === 0 ? "#003664" : "#e3efff",
              borderRadius: index === 0 ? "10px 10px 0 0" : "0",
            });
            sheet.appendChild(header);
          });
        }
      }
      const rows = document.createElement("div");
      Object.assign(rows.style, {
        position: "relative",
        width: `${sheetWidth}px`,
        height: `${capacity * layout.lineHeight}px`,
      });
      for (let row = 0; row < capacity; row += 1) {
        const absoluteLine = firstLine + row + 1;
        const marker = document.createElement("div");
        marker.textContent = isMinistry
          ? row === 0
            ? `(제1문-${pageIndex + 1}쪽) ${absoluteLine}`
            : absoluteLine % 5 === 0
              ? String(absoluteLine)
              : ""
          : row % 2 === 0
            ? `${row + 1} ${row === 0 ? `(${pageIndex + 1}쪽)` : ""}`
            : "";
        Object.assign(marker.style, {
          boxSizing: "border-box",
          width: `${layout.gutterWidth}px`,
          height: `${layout.lineHeight}px`,
          lineHeight: `${layout.lineHeight}px`,
          color: "#5f6b7a",
          fontSize: isMinistry ? "10px" : "11px",
          fontWeight: isMinistry ? "700" : "400",
          whiteSpace: "nowrap",
          textAlign: isMinistry ? "right" : "left",
          paddingRight: isMinistry ? "10px" : "0",
        });
        rows.appendChild(marker);
      }
      const viewport = document.createElement("div");
      // 한 줄씩 다시 문자열을 만들지 않고, 연속된 원본 답안을 30/34행
      // 높이로 잘라 보여 준다. 문단이 쪽을 넘어가도 정렬과 줄바꿈이 유지된다.
      Object.assign(viewport.style, {
        position: "absolute",
        top: "0",
        left: `${layout.gutterWidth}px`,
        width: `${layout.width}px`,
        height: `${capacity * layout.lineHeight}px`,
        overflow: "hidden",
        backgroundImage: isMinistry
          ? `linear-gradient(${pageIndex % 2 === 0 ? "rgba(247, 247, 247, 0.8), rgba(247, 247, 247, 0.8)" : "rgba(227, 235, 241, 0.8), rgba(227, 235, 241, 0.8)"}), repeating-linear-gradient(transparent, transparent 23px, rgb(25, 22, 43) 23px, rgb(25, 22, 43) 24px)`
          : `repeating-linear-gradient(transparent, transparent 39px, rgb(238, 238, 238) 39px, rgb(238, 238, 238) 40px), linear-gradient(${pageIndex % 2 === 0 ? "#ffffff, #ffffff" : "#f3f7ff, #f3f7ff"})`,
      });
      const content = flow.cloneNode(true);
      Object.assign(content.style, {
        position: "absolute",
        left: `${layout.padding}px`,
        top: `${-firstLine * layout.lineHeight}px`,
      });
      viewport.appendChild(content);
      rows.appendChild(viewport);
      sheet.appendChild(rows);
      page.appendChild(sheet);
      root.appendChild(page);
      firstLine += capacity;
    }
    return root;
  } finally {
    root.remove();
  }
};

const loadInlineFont = async () => {
  const response = await fetch(getFontUrl());
  if (!response.ok)
    throw new Error("PDF용 글꼴을 불러오지 못했습니다. 다시 시도해주세요.");
  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("PDF용 글꼴을 읽지 못했습니다."));
    reader.readAsDataURL(blob);
  });
};

const renderAnswerPage = async (page, inlineFont) => {
  // html2canvas의 텍스트 재그리기는 Noto의 기준선을 달리 계산하고 반복
  // 배경선을 생략한다. 자체 완결된 SVG 안에서 브라우저가 HTML을 직접
  // 그리게 해 원본의 글자 위치, 자간, 밑줄과 페이지 클리핑을 보존한다.
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("width", String(A4_WIDTH_PX));
  svg.setAttribute("height", String(A4_HEIGHT_PX));
  const foreignObject = document.createElementNS(
    "http://www.w3.org/2000/svg",
    "foreignObject",
  );
  foreignObject.setAttribute("width", "100%");
  foreignObject.setAttribute("height", "100%");
  const content = page.cloneNode(true);
  // SVG는 앱의 전역 CSS를 상속하지 않는다. 특히 Chakra의 font-feature-settings
  // ("kern") 누락은 같은 글꼴에서도 영문/공백 폭을 바꾸므로 계산된 스타일을
  // 명시적으로 옮겨 이미지 내부의 재배치를 방지한다.
  const originals = [page, ...page.querySelectorAll("*")];
  const copies = [content, ...content.querySelectorAll("*")];
  originals.forEach((original, index) => {
    const computed = window.getComputedStyle(original);
    for (const property of computed) {
      copies[index].style.setProperty(
        property,
        computed.getPropertyValue(property),
      );
    }
  });
  content.style.fontFamily = '"Noto Sans KR", "Malgun Gothic", sans-serif';
  content.prepend(createFontStyle(inlineFont));
  foreignObject.appendChild(content);
  svg.appendChild(foreignObject);
  const source = new XMLSerializer().serializeToString(svg);
  const img = new Image();
  await new Promise((resolve, reject) => {
    img.onload = resolve;
    img.onerror = () =>
      reject(new Error("PDF 답안지 이미지를 생성하지 못했습니다."));
    // SVG 이미지 안에서는 외부 글꼴을 읽을 수 없다. 글꼴은 위에서 data URL로
    // 내장했으며, 답안 HTML에는 외부 이미지나 실행 가능한 속성이 없다.
    img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(source)}`;
  });
  const canvas = document.createElement("canvas");
  canvas.width = A4_WIDTH_PX * 2;
  canvas.height = A4_HEIGHT_PX * 2;
  const context = canvas.getContext("2d");
  if (!context)
    throw new Error("이 브라우저에서는 PDF 이미지를 생성할 수 없습니다.");
  context.scale(2, 2);
  context.drawImage(img, 0, 0);
  return canvas;
};

const createAnswerPdfBlob = async ({ html, subject, templateType }) => {
  const [inlineFont, JsPdf, root] = await Promise.all([
    loadInlineFont(),
    loadJsPdf(),
    createAnswerSheetElement({ html, subject, templateType }),
  ]);
  document.body.appendChild(root);
  try {
    const pdf = new JsPdf({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
      compress: true,
    });
    const pages = Array.from(
      root.querySelectorAll("[data-answer-export-page]"),
    );
    for (let index = 0; index < pages.length; index += 1) {
      const canvas = await renderAnswerPage(pages[index], inlineFont);
      if (index > 0) pdf.addPage("a4", "portrait");
      pdf.addImage(
        canvas.toDataURL("image/png"),
        "PNG",
        0,
        0,
        210,
        297,
        undefined,
        "FAST",
      );
    }
    return pdf.output("blob");
  } finally {
    root.remove();
  }
};

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const exportAnswerToPdf = async ({ html, subject, templateType }) => {
  const blob = await createAnswerPdfBlob({ html, subject, templateType });
  const filename = sanitizePdfFilename(subject);
  downloadBlob(blob, filename);
  return filename;
};

const printAnswerPdf = async ({ html, subject, templateType }) => {
  const blob = await createAnswerPdfBlob({ html, subject, templateType });
  const url = URL.createObjectURL(blob);
  const iframe = document.createElement("iframe");
  iframe.title = `${subject || "답안지"} PDF 인쇄`;
  Object.assign(iframe.style, {
    position: "fixed",
    width: "1px",
    height: "1px",
    right: "0",
    bottom: "0",
    border: "0",
  });
  iframe.onload = () => {
    window.setTimeout(() => {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
    }, 250);
  };
  iframe.src = url;
  document.body.appendChild(iframe);
  window.setTimeout(() => {
    iframe.remove();
    URL.revokeObjectURL(url);
  }, 60000);
};

const printAnswerInWordLayout = async ({ html, subject, templateType }) => {
  const printWindow = window.open("", "_blank");
  if (!printWindow)
    throw new Error("인쇄 창이 차단되었습니다. 팝업을 허용해주세요.");
  printWindow.opener = null;
  try {
    const root = await createAnswerSheetElement({
      html,
      subject,
      templateType,
    });
    Object.assign(root.style, {
      position: "static",
      left: "auto",
      top: "auto",
    });
    printWindow.document.title = `${subject || "답안지"} 답안지`;
    printWindow.document.body.style.margin = "0";
    printWindow.document.body.appendChild(root);
    const style = printWindow.document.createElement("style");
    style.textContent = `
      @page { size: A4 portrait; margin: 0; }
      @media print {
        html, body { margin: 0 !important; padding: 0 !important; }
        * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
      }
    `;
    printWindow.document.head.appendChild(style);
    await waitForAnswerFont(
      printWindow.document,
      getAnswerLayout(templateType),
    );
    printWindow.focus();
    printWindow.print();
  } catch (error) {
    printWindow.close();
    throw error;
  }
};

module.exports = {
  createAnswerPdfBlob,
  createAnswerSheetElement,
  exportAnswerToPdf,
  printAnswerInWordLayout,
  printAnswerPdf,
  sanitizePdfFilename,
};
